import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import intl from 'react-intl-universal';
import get from 'lodash/get';
import moment from 'moment';
import classnames from 'classnames';
import {
  Icon,
  Button,
  AvatarGroup,
  Table,
  tailwindOverride,
  Modal,
  Typography,
} from '@getsynapse/design-system';
import { Request } from 'utils/customTypes';
import { DATE } from 'utils/constants';
import { formatRequestIdentifier } from 'Pages/helpers';
import { isUserStatusDisabled } from 'utils/functions';
import RequestStatusTag from 'Atoms/RequestStatusTag/RequestStatusTag';
import { RequestStatus } from 'utils/types/request';
import {
  userSummarySelectors,
  getUserSummaries,
} from 'state/normalized/userSummaries/userSummariesSlice';
import { UserSummary } from 'types/store/normalized/userSummary';
import {
  businessUnitSummarySelectors,
  getBusinessUnitSummaries,
} from 'state/normalized/businessUnitSummaries/businessUnitSummariesSlice';
import unlinkIcon from 'assets/icons/unlink.svg';
import { updateProjectLinkedRequests } from 'state/Project/projectSlice';

import { showNotification as showSnackbarNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';

type LinkedProjectRequestsTableProps = {
  projectId: string;
  requestsList: Request[];
  viewRequest: (request: Request) => void;
  disabled?: boolean;
};

const LinkedProjectRequestsTable: React.FC<LinkedProjectRequestsTableProps> = ({
  requestsList = [],
  projectId,
  viewRequest,
  disabled = false,
}) => {
  const dispatch = useDispatch();

  const [isUnlinkModalOpen, setIsUnlinkModalOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(
    null
  );

  // Lookups happen inside the row map below, so select the adapter-memoized
  // entity dictionaries once. A per-id getter selector would return a new
  // closure on every store change and re-render this table on every action.
  const userSummaries = useSelector(userSummarySelectors.selectEntities);
  const businessUnitSummaries = useSelector(
    businessUnitSummarySelectors.selectEntities
  );

  const mapToAvatar = useCallback(
    (user: UserSummary) => ({
      imageSrc: user.avatarUrl || '',
      initial: user.avatar?.initial || '',
      name: user.name,
      disabled: isUserStatusDisabled(user.status),
    }),
    []
  );

  const mapToAvatarGroup = useCallback(
    (owners: UserSummary[]) => owners.map(mapToAvatar),
    [mapToAvatar]
  );

  const getRequestOwners = useCallback(
    (owners: UserSummary[]) =>
      owners.length === 1 ? (
        <div className='flex items-center'>
          <AvatarGroup
            avatars={mapToAvatarGroup(owners)}
            deactivatedText={intl.get('DEACTIVATED')}
          />
          <span className='ml-2.5 truncate'>{owners[0].name}</span>
        </div>
      ) : (
        <AvatarGroup
          avatars={mapToAvatarGroup(owners)}
          deactivatedText={intl.get('DEACTIVATED')}
        />
      ),
    [mapToAvatarGroup]
  );

  const rows = useMemo(
    () =>
      requestsList?.map((request) => {
        const requester = userSummaries[request.requester_id!];
        const owners = ((request.ownerIds as string[]) ?? [])
          .map((ownerId) => userSummaries[ownerId])
          .filter((owner): owner is UserSummary => owner !== undefined);
        const businessUnit =
          businessUnitSummaries[get(request, 'business_team_id') as string];

        return {
          className: 'cursor-pointer table w-full table-fixed',
          cells: [
            {
              content: formatRequestIdentifier(request?.requestIdentifier!),
              className: 'w-30',
            },
            {
              content: getRequestOwners(owners),
              className: 'w-48',
            },
            {
              content: requester && requester?.name,
              className: tailwindOverride('w-36 truncate', {
                'text-neutral-light': isUserStatusDisabled(
                  request.requester?.status
                ),
              }),
            },
            {
              content: (
                <div className='flex items-center'>
                  {request?.isArchived && (
                    <div className='flex items-center justify-center'>
                      <Icon
                        name='archive'
                        className='text-primary-dark w-3 h-3 mr-2.5'
                      />
                    </div>
                  )}
                  <Typography className='truncate'>{request?.title}</Typography>
                </div>
              ),
              className: 'w-1/6',
            },
            {
              content: businessUnit?.name,
              className: 'w-10% truncate',
            },
            {
              content: moment(request?.submittedAt).format(DATE.SHORT_FORMAT),
              className: 'w-30',
            },
            {
              content: (
                <RequestStatusTag status={request.status as RequestStatus} />
              ),
              className: 'w-24',
            },
            {
              content: (
                <div className='flex'>
                  <Button
                    className='text-sm text-primary'
                    variant='tertiary'
                    onClick={() => viewRequest(request)}
                    data-cy={`view-linked-request-${request.id}`}
                  >
                    {intl.get('PROJECT_DETAIL.LINKED_REQUESTS_SECTION.VIEW')}
                  </Button>
                  <Button
                    className='text-sm text-primary'
                    variant='tertiary'
                    onClick={() => {
                      setIsUnlinkModalOpen(true);
                      setSelectedRequestId(request.id!);
                    }}
                    data-cy={`unlink-request-${request.id}`}
                    disabled={disabled}
                  >
                    {intl.get('REQUEST_PAGE.TOP_BAR.LINK_REQUEST_MODAL.UNLINK')}
                  </Button>
                </div>
              ),
              className: 'w-36',
            },
          ],
        };
      }),
    [
      businessUnitSummaries,
      disabled,
      getRequestOwners,
      userSummaries,
      requestsList,
      viewRequest,
    ]
  );

  useEffect(() => {
    if (!requestsList.length) return;

    const ownerIds = requestsList.flatMap(
      (request) => (request.ownerIds as string[]) || []
    );
    const requesterIds = requestsList.map((request) => request.requester_id!);

    const uniqueUserIds = [...new Set([...ownerIds, ...requesterIds])];

    if (uniqueUserIds.length > 0) {
      dispatch(
        getUserSummaries({
          userIds: uniqueUserIds,
        })
      );
    }

    const businessUnitIds = requestsList.map((request) =>
      get(request, 'business_team_id')
    );

    if (businessUnitIds.length > 0) {
      dispatch(
        getBusinessUnitSummaries({
          businessUnitIds,
        })
      );
    }
  }, [requestsList, dispatch]);

  const unLinkRequest = async () => {
    setIsUnlinkModalOpen(false);

    if (!selectedRequestId) return;

    try {
      const newRequestsList = requestsList.filter(
        (request) => request.id !== selectedRequestId
      );

      await dispatch(
        updateProjectLinkedRequests({
          projectId,
          requestIds: newRequestsList?.map((request) => request.id!),
        })
      );
      dispatch(
        showSnackbarNotification({
          notificationVariant: 'success',
          notificationTitle: intl.get(
            'PROJECT_DETAIL.LINKED_REQUESTS_SECTION.UNLINK_SUCCESS_MESSAGE'
          ),
        })
      );
    } catch {
      dispatch(
        showSnackbarNotification({
          notificationVariant: 'error',
          notificationTitle: intl.get(
            'PROJECT_DETAIL.LINKED_REQUESTS_SECTION.UNLINK_ERROR_TITLE'
          ),
          notificationMessage: intl.get(
            'PROJECT_DETAIL.LINKED_REQUESTS_SECTION.ERROR_MESSAGE_RELOAD'
          ),
          autoHide: false,
        })
      );
    }
  };

  const closeModal = () => setIsUnlinkModalOpen(false);

  return (
    <div className='w-full rounded-t'>
      <Modal
        title={intl.get(
          'PROJECT_DETAIL.LINKED_REQUESTS_SECTION.UNLINK_REQUEST'
        )}
        data-cy='unlink-modal'
        titleIcon={{ src: unlinkIcon }}
        isOpen={isUnlinkModalOpen}
        aria-label={intl.get(
          'PROJECT_DETAIL.LINKED_REQUESTS_SECTION.UNLINK_REQUEST'
        )}
        closeModal={closeModal}
        actionButtons={[
          {
            children: intl.get('UNLINK'),
            color: 'warning',
            onClick: unLinkRequest,
            'data-cy': 'unlink-modal__unlink-button',
          },
          {
            children: intl.get('CANCEL'),
            'data-cy': 'cancel-unlink-button',
            variant: 'tertiary',
            onClick: closeModal,
          },
        ]}
      />
      <Table
        data-cy='linked-requests-table'
        className='w-full mb-5 rounded'
        canSelectRows={false}
        emptyComponent={
          <div className='h-12 flex justify-center items-center text-neutral'>
            {intl.get(
              'PROJECT_DETAIL.LINKED_REQUESTS_SECTION.NO_REQUEST_LINKED'
            )}
          </div>
        }
        bodyProps={{
          className: classnames({
            block: requestsList.length > 0,
          }),
        }}
        data={{
          headData: {
            className: 'w-full block h-auto min-h-10',
            rowProps: {
              className: 'table w-full table-fixed min-h-10',
            },
            headCells: [
              {
                content: intl.get(
                  'PROJECT_DETAIL.LINKED_REQUESTS_SECTION.REQUEST_NUMBER'
                ),
                className: 'rounded-tl w-30',
              },
              {
                content: intl.get(
                  'PROJECT_DETAIL.LINKED_REQUESTS_SECTION.REQUEST_OWNER'
                ),
                className: 'w-48',
              },
              {
                content: intl.get(
                  'PROJECT_DETAIL.LINKED_REQUESTS_SECTION.REQUESTER_NAME'
                ),
                className: 'w-36',
              },
              {
                content: intl.get(
                  'PROJECT_DETAIL.LINKED_REQUESTS_SECTION.REQUEST_TITLE'
                ),
                className: 'w-1/6',
              },
              {
                content: intl.get(
                  'PROJECT_DETAIL.LINKED_REQUESTS_SECTION.BUSINESS_UNIT'
                ),
                className: 'w-10%',
              },
              {
                content: intl.get(
                  'PROJECT_DETAIL.LINKED_REQUESTS_SECTION.SUBMISSION_DATE'
                ),
                className: 'w-30',
              },
              {
                content: intl.get(
                  'PROJECT_DETAIL.LINKED_REQUESTS_SECTION.STATUS'
                ),
                className: 'w-24',
              },
              {
                content: '',
                className: 'w-36 rounded-tr',
              },
            ],
          },
          rows,
        }}
      />
    </div>
  );
};

export default LinkedProjectRequestsTable;
